import React, { useEffect, useMemo, useState } from "react";
import {
  convertSnakeToCamelCase,
  rgba2hex,
  useActiveCanvas,
} from "@/utils/utils";
import {
  Tabs as TabsUnstyled,
  TabsList as TabsListUnstyled,
  Tab as TabUnstyled,
  TabPanel as TabPanelUnstyled,
} from "@mui/base";
import {
  CSS_VALUES_TO_COLLECT,
  EXPANDABLE_CSS_VALUES,
  SCREEN_SIZE_NAME_TO_SIZE,
} from "@/utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
  selectMultiSelectedTempoElements,
  selectSelectedTempoElement,
  setInspectElementId,
} from "@/store/iframeSlice";
import PropManager from "./PropManager";
import { convertExpandedDefinedStyles } from "@/utils/cssValueUtils";
import { TempoElement } from "@/utils/tempoElement";
import {
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/store/zustand/canvasStore";
import { X } from "lucide-react";
import useLayoutStore from "@/store/zustand/layoutStore";
import useModifiers, { ALL_MODIFIERS } from "@/hooks/useModifiers";
import useUpdateStoryboardSize from "../Canvas/hooks/useUpdateStoryboardSize";
import { ClassType } from "@/lib/tailwind/properties";
import useDebouncedState from "@/hooks/useDebouncedState";
import { TailwindStylingPanel } from "./TailwindStylingPanelV2";
import { JsxElement } from "@/utils/jsxElementTypes";
import { useTypedSelector } from "@/store/store";
import { useIframeStore } from "@/store/zustand/iframeStore";
import { NavTreeNode } from "@/utils/types";

// Matches tempo-devtools, used for instant updates
const TEMPO_INSTANT_UPDATE_STYLING_PREFIX = "arb89-styling-";
const MESSAGE_AGGREGATE_TIME_MS = 100;

interface Props {
  selectedTreeElement: JsxElement;

  cssElementLookup: any;

  applyChangeToElement: (
    styleChange: { [styleKey: string]: string },
    modifiers: string[],
    customProperties: ClassType[]
  ) => void;
}

// The value of a css field
export interface CssFieldValue {
  cssName: string;
  evaluatedValue?: string;
}

/**
 * CSS Rule returned from the iframes
 */
export interface CssRule {
  filename?: string;
  selector?: string;
  atrule?: string;
  codebaseId?: string;
  source?: any;
  styles?: { [cssKey: string]: string };
  inherited?: boolean;
  applied?: boolean; // Whether this css rule is applied to the current element
  removable?: boolean; // Whether this css rule is removable with a simple class change
}

const RightStyle: React.FC<Props> = (props) => {
  const selectedElement: TempoElement = useSelector(selectSelectedTempoElement);

  // TODO: get rid of this and just track it from the backend?
  // This is the cache so that when a user makes a change it immediately shows in the styling panel
  const [styleSet, setStyleSet] = useState({});

  const extractedCssRules: CssRule[] = useSelector(
    (state: any) => state.iframe.processedCssRules
  );
  const cssEvals: { [cssName: string]: string } = useSelector(
    (state: any) => state.iframe.cssEvals
  );

  const { modifiers, addModifier, removeModifier, clearModifiers } =
    useModifiers([]);

  const iframeChangedKey = useSelector(
    (state: any) => state.iframe.iframeChangedKey
  );
  const forceReloadKey = useSelector(
    (state: any) => state.iframe.forceReloadKey
  );
  const iframeState = useSelector((state: any) => state.iframe.iframeState);
  const classList = useTypedSelector((state) => state.iframe.elementClassList);

  const selectedNavTreeNode = useSelectedNavTreeNode();
  const collapseRight = useLayoutStore((state) => state.collapseRight);

  const sendMessageToIFrame = useIframeStore(
    (store) => store.sendMessageToIFrame
  );

  const dispatch = useDispatch();

  const canvas = useActiveCanvas();

  const { setSize } = useUpdateStoryboardSize(canvas);

  const [previousWidths, setPreviousWidths] = useState<{
    [key: string]: number;
  }>({});

  const [lastActiveStoryboardId, setLastActiveStoryboardId] = useState(null);

  const [iframeValuesDebounced, setIFrameValuesDebounced] = useDebouncedState(
    {
      cssEvals,
      classList,
    },
    MESSAGE_AGGREGATE_TIME_MS
  );

  useEffect(() => {
    setIFrameValuesDebounced({ cssEvals, classList });
  }, [cssEvals, classList, setIFrameValuesDebounced]);

  useEffect(() => {
    // Identify if there is a size-related modifier
    const sizeModifier = modifiers.find((modifier) =>
      ALL_MODIFIERS.size.includes(modifier)
    );
    const currentStoryboardId = selectedElement?.storyboardId;
    const currentStoryboard = canvas?.storyboards?.find(
      (s) => s.id === currentStoryboardId
    );

    // Function to revert to previous sizes and clear the storage
    const revertAndClearPreviousWidths = () => {
      Object.entries(previousWidths).forEach(([storyboardId, width]) => {
        const storyboard = canvas?.storyboards.find(
          (s) => s.id === storyboardId
        );
        if (storyboard) {
          setSize(storyboard, width, storyboard.height);
        }
      });
      setPreviousWidths({});
    };

    // Always check for conditions to revert first
    if (
      !sizeModifier ||
      !currentStoryboard ||
      selectedElement.isEmpty() ||
      currentStoryboardId !== lastActiveStoryboardId
    ) {
      revertAndClearPreviousWidths();
    }

    if (sizeModifier && currentStoryboard && !selectedElement.isEmpty()) {
      // Setting new size
      const newSize = SCREEN_SIZE_NAME_TO_SIZE[sizeModifier];
      if (newSize) {
        // Store the current width before updating
        const currentWidth = currentStoryboard?.width;
        setPreviousWidths((prevWidths) => ({
          ...prevWidths,
          [currentStoryboardId]: currentWidth,
        }));

        // Apply the new size
        setSize(currentStoryboard, newSize.width, null);
        // @ts-expect-error TS2345
        setLastActiveStoryboardId(currentStoryboardId);
      }
    }
  }, [selectedElement.getKey(), modifiers]); // React to changes in selectedElement and modifiers

  const [activeTab, setActiveTab] = useState("styles");

  // For inspecting state/props
  const inspectElementResponse = useSelector(
    (state: any) => state.iframe.inspectElementResponse
  );
  const inspectElementId = useSelector(
    (state: any) => state.iframe.currentInspectElementId
  );

  /**
   * Get the props & state for the selected component
   */
  useEffect(() => {
    const selectedElementChanged =
      selectedElement.codebaseId !== inspectElementId;
    if (
      selectedElement.isEmpty() ||
      (inspectElementResponse && !selectedElementChanged)
    ) {
      return;
    }

    let payload = {};
    if (selectedNavTreeNode?.isComponent) {
      payload = {
        isComponent: true,
        componentName: props.selectedTreeElement?.componentName,
        tempoElementID: selectedElement.codebaseId,
      };
    } else {
      let firstComponentAboveThisElement: NavTreeNode | null =
        selectedNavTreeNode;
      while (
        firstComponentAboveThisElement &&
        !firstComponentAboveThisElement.isComponent
      ) {
        firstComponentAboveThisElement =
          firstComponentAboveThisElement.parent || null;
      }

      payload = {
        isComponent: false,
        tempoElementID: selectedElement.codebaseId,
        componentName: props.selectedTreeElement?.componentName,
        componentElementId:
          firstComponentAboveThisElement?.tempoElement.codebaseId,
      };
    }
    sendMessageToIFrame({
      id: selectedElement.codebaseId,
      type: "inspectElement",
      payload,
    });

    dispatch(setInspectElementId(selectedElement.codebaseId));
  }, [
    props.selectedTreeElement,
    selectedNavTreeNode,
    selectedElement,
    forceReloadKey,
  ]);

  /**
   * Clear modifiers if the selected element has changed
   */
  useEffect(() => {
    clearModifiers();
  }, [selectedElement]);

  useEffect(() => {
    sendMessageToIFrame(
      {
        type: "executeFunction",
        payload: {
          functionName: "setModifiersForSelectedElement",
          args: [modifiers, selectedElement.getKey()],
        },
      },
      selectedElement.storyboardId
    );
  }, [
    modifiers,
    selectedElement,
    props.cssElementLookup,
    iframeState,
    iframeChangedKey,
    selectedNavTreeNode,
  ]);

  /**
   * Fetch the latest css styles on any changes
   */
  useEffect(() => {
    if (selectedElement.isEmpty()) {
      return;
    }

    const refetchCssValues = () => {
      sendMessageToIFrame(
        {
          type: "executeFunction",
          payload: {
            functionName: "getCssEvals",
            args: [selectedElement.getKey()],
          },
        },
        selectedElement.storyboardId
      );
      sendMessageToIFrame(
        {
          type: "executeFunction",
          payload: {
            functionName: "getElementClassList",
            args: [selectedElement.getKey()],
          },
        },
        selectedElement.storyboardId
      );
    };

    refetchCssValues();
  }, [
    selectedElement,
    props.cssElementLookup,
    iframeState,
    iframeChangedKey,
    selectedNavTreeNode,
    modifiers,
  ]);

  /**
   * This use memo processes all the info taken from the iframe and converts
   * it into a format required by the styling panel
   */
  const {
    cssNameToFieldValue,
    parentCssEvals,
  }: {
    cssNameToFieldValue: { [cssName: string]: CssFieldValue };
    parentCssEvals: { [cssName: string]: string };
  } = useMemo(() => {
    const { cssEvals } = iframeValuesDebounced;

    // The values of the css fields as evaluated from the DOM
    const cssNameToFieldValue: { [cssName: string]: CssFieldValue } = {};

    CSS_VALUES_TO_COLLECT.forEach((cssName: string) => {
      cssNameToFieldValue[cssName] = {
        cssName,
      };

      // For styles that can be set in different ways (e.g. padding-horizontal, padding, padding-left/right, etc)
      // Convert them out to their individual parts (top, right, bottom, left)
      let cssEvalsStyling = cssEvals?.[cssName] || null;

      // If it is expandable, the try to get the value from the individual parts if those are defined
      // E.g. we can get padding-right from the padding value
      if (EXPANDABLE_CSS_VALUES.has(cssName)) {
        // We also expand out the css evals
        cssEvalsStyling = convertExpandedDefinedStyles(
          cssName,
          undefined,
          cssEvals
        );
      }

      cssNameToFieldValue[cssName].evaluatedValue =
        cssEvalsStyling || undefined;

      // Always use the evaluated color because it will always be an rgba.
      if (cssName.includes("color")) {
        cssNameToFieldValue[cssName].evaluatedValue = rgba2hex(cssEvalsStyling);
      }
    });

    const parentCssEvals: { [cssName: string]: string } = cssEvals?.[
      "parent"
    ] as unknown as { [cssName: string]: string };

    return { cssNameToFieldValue, parentCssEvals };
  }, [
    props.selectedTreeElement,
    iframeChangedKey,
    iframeValuesDebounced.cssEvals,
  ]);

  return (
    <div
      className="right-style hide-scrollbar"
      data-mouse-down-id="styling-panel"
    >
      <TabsUnstyled
        defaultValue={"styles"}
        value={activeTab}
        onChange={(e: any, val: string) => val && setActiveTab(val)}
      >
        <TabsListUnstyled className="tab-list w-full">
          <TabUnstyled className="tab" value={"styles"}>
            Styles
          </TabUnstyled>
          <TabUnstyled className="tab" value={"props"}>
            Properties
          </TabUnstyled>
          <div className="flex-grow flex flex-row justify-end items-center pr-4">
            <X
              className="w-4 h-4 cursor-pointer text-secondary-800 hover:text-secondary-900"
              onClick={() => collapseRight()}
            />
          </div>
        </TabsListUnstyled>
        <TabPanelUnstyled value={"styles"}>
          <TailwindStylingPanel
            cssValues={cssNameToFieldValue}
            parentCssEvals={parentCssEvals}
            selectedTreeElement={props.selectedTreeElement}
          />
        </TabPanelUnstyled>
        <TabPanelUnstyled value={"props"}>
          <PropManager
            inspectElementResponse={inspectElementResponse}
            selectedTreeElement={props.selectedTreeElement}
            loadingProps={Boolean(
              !selectedElement.isEmpty() && !inspectElementResponse
            )}
          />
        </TabPanelUnstyled>
      </TabsUnstyled>
    </div>
  );
};

export default React.memo(RightStyle);
